import * as Sentry from '@sentry/react';
// import { reactRouterV6Instrumentation } from '@sentry/react';
// import { useEffect } from 'react';
// import {
//   createRoutesFromChildren,
//   matchRoutes,
//   useLocation,
//   useNavigationType,
// } from 'react-router-dom';
import {
  initConsoleCapture,
  destroyConsoleCapture,
  flushConsoleLogs,
  type ConsoleCaptureConfig,
} from './console-capture';

// ============================================
// 日志分类配置
// ============================================

// 关键日志前缀白名单 - 这些日志始终会被记录和上报
const CRITICAL_LOG_PREFIXES = [
  'canvas_operation',
  '[AI]',
  '[Agent]',
  '[Sync]',
  '[Auth]',
  '[Network]',
  '[Critical]',
  '[Important]',
  '######', // 开发调试关键标记
];

// 噪音日志前缀黑名单 - 这些日志会被过滤掉
const NOISE_LOG_PREFIXES = [
  '[HMR]',
  '[vite]',
  '[webpack]',
  'Download the React DevTools',
  '[Performance]', // 高频性能日志
  '[Render]', // 高频渲染日志
  '[Animation]', // 动画相关
];

// 噪音日志正则匹配 - 匹配这些模式的日志会被过滤
const NOISE_LOG_PATTERNS = [
  /^%c/, // styled console logs
  /^\[object Object\]$/, // 无意义的对象字符串
  /^undefined$/, // undefined 输出
  /^null$/, // null 输出
  /hot.*update/i, // HMR 更新
  /websocket.*connect/i, // WebSocket 频繁连接日志
];

// Console 日志捕获配置
const DEFAULT_CONSOLE_CAPTURE_CONFIG: Partial<ConsoleCaptureConfig> = {
  enabled: true,
  levels: ['log', 'info', 'warning', 'error', 'debug'], // 启用 debug 级别
  bufferSize: 100,
  autoFlushInterval: 60000, // 1分钟自动上传
  maxMessageLength: 2000,
  preserveOriginal: true,
  // 前缀过滤（黑名单）
  excludePrefixes: NOISE_LOG_PREFIXES,
  // 正则过滤（黑名单）
  excludePatterns: NOISE_LOG_PATTERNS,
  // 关键日志前缀（白名单）
  criticalPrefixes: CRITICAL_LOG_PREFIXES,
  // 自定义过滤函数
  filter: (level, args) => {
    const firstArg = args[0];
    if (typeof firstArg !== 'string') return true;

    // 关键日志白名单 - 始终保留
    for (const prefix of CRITICAL_LOG_PREFIXES) {
      if (firstArg.includes(prefix)) {
        return true;
      }
    }

    // 过滤掉高频但无意义的日志
    if (firstArg.includes('[Performance]') && level === 'debug') {
      return false;
    }

    return true;
  },
};

function createSentry() {
  let client: Sentry.BrowserClient | undefined;
  let consoleCaptureEnabled = false;

  const wrapped = {
    init(consoleCaptureConfig?: Partial<ConsoleCaptureConfig>) {
      if (BUILD_CONFIG.SENTRY_DSN && !client) {
        // https://docs.sentry.io/platforms/javascript/guides/react/#configure
        client = Sentry.init({
          dsn: BUILD_CONFIG.SENTRY_DSN,
          debug: BUILD_CONFIG.debug ?? false,
          environment: BUILD_CONFIG.appBuildType,
          integrations: defaults => [
            ...defaults.filter(
              integration => integration.name !== 'Console'
            ),
            Sentry.browserTracingIntegration({}),
            Sentry.browserProfilingIntegration(),
            Sentry.replayIntegration({
              maskAllText: false,
              blockAllMedia: false,
              networkDetailAllowUrls: [
                "https://api.muset.ai",
                "https://api-va.muset.ai",
                "wss://api-va.muset.ai",
                "https://muset-va-dev.wenxiaobai.com"
              ],
            }),
          ],
          replaysSessionSampleRate: 1.0,
          // 错误事件采样率 - 100% 全量上报
          sampleRate: 1.0,
          // 性能追踪采样率 - 100% 全量上报
          tracesSampleRate: 1.0,
          // 性能分析采样率 - 100% 全量上报
          profilesSampleRate: 1.0,
          sendDefaultPii: true,
          // 增加 breadcrumbs 最大数量，以便记录更多日志
          maxBreadcrumbs: 200,
          // 控制哪些 URL 启用追踪
          tracePropagationTargets: [
            'localhost',
            /^https:\/\/.*\.wenxiaobai\.com/,
            /^https:\/\/.*\.muset\.ai/,
          ],
          // 在发送事件前的钩子，可以添加额外信息
          beforeSend(event, _hint) {
            // 确保所有事件都带有 console logs 上下文
            return event;
          },
        }) as Sentry.BrowserClient;

        Sentry.setTags({
          distribution: BUILD_CONFIG.distribution,
          appVersion: BUILD_CONFIG.appVersion,
          editorVersion: BUILD_CONFIG.editorVersion,
        });

        // 初始化 Console 日志捕获
        const mergedConfig = {
          ...DEFAULT_CONSOLE_CAPTURE_CONFIG,
          ...consoleCaptureConfig,
        };
        initConsoleCapture(mergedConfig);
        consoleCaptureEnabled = true;
      }
    },

    enable() {
      if (client) {
        client.getOptions().enabled = true;
      }
    },

    disable() {
      if (client) {
        client.getOptions().enabled = false;
      }
    },

    /**
     * 手动上传 Console 日志
     * 可以在关键操作前后调用，确保日志被及时上传
     */
    flushConsoleLogs() {
      if (consoleCaptureEnabled) {
        flushConsoleLogs();
      }
    },

    /**
     * 销毁 Console 日志捕获
     */
    destroyConsoleCapture() {
      if (consoleCaptureEnabled) {
        destroyConsoleCapture();
        consoleCaptureEnabled = false;
      }
    },
  };

  return wrapped;
}

export const sentry = createSentry();
